/**
 * Carte mise en avant pour la création de bon cadeau sur-mesure
 *
 * Design distinct des cartes de bons cadeaux classiques pour attirer l'attention
 * et encourager les utilisateurs à créer un bon personnalisé.
 *
 * @module CustomVoucherCard
 */

import React from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Stack,
  alpha,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import Iconify from 'packages/common/components/iconify';
import {getContrastTextColor} from 'packages/common/utils/colorUtils';

/**
 * Composant de fond animé avec gradient
 */
const AnimatedBackground = styled(Box)(({theme, primarycolor}) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `linear-gradient(135deg,
    ${alpha(primarycolor, 0.08)} 0%,
    ${alpha(primarycolor, 0.04)} 50%,
    ${alpha(primarycolor, 0.08)} 100%)`,
}));

/**
 * Carte du bon cadeau sur-mesure
 *
 * @param {Object} props - Props du composant
 * @param {Object} props.companyDetails - Détails de l'établissement
 * @param {Function} props.onNavigate - Callback de navigation déclenché au clic
 * @return {JSX.Element} Carte du bon cadeau sur-mesure
 */
export function CustomVoucherCard({companyDetails, onNavigate}) {
  const {t} = useTranslation();
  const primaryColor = companyDetails?.primaryColor || companyDetails?.branding?.primaryColor || '#1a237e';

  const handleClick = () => {
    if (onNavigate) onNavigate();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`${t('common.giftVoucher.customVoucher.title', 'Composez votre bon cadeau')} - ${t('common.giftVoucher.customVoucher.description', 'Créez un bon sur-mesure')}`}
      onKeyDown={handleKeyDown}
      sx={{
        'position': 'relative',
        'width': '100%',
        'maxWidth': 450,
        'height': '100%',
        '&:focus': {outline: 'none'},
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: primaryColor,
          outlineOffset: '4px',
          borderRadius: '12px',
        },
      }}
    >
      <Card
        sx={{
          'width': '100%',
          'height': 'auto',
          'cursor': 'pointer',
          'overflow': 'hidden',
          'border': `2px solid ${alpha(primaryColor, 0.3)}`,
          'borderRadius': 3,
          'transition': 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          'display': 'flex',
          'flexDirection': 'column',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 12px 40px ${alpha(primaryColor, 0.25)}`,
            borderColor: primaryColor,
          },
        }}
        onClick={handleClick}
      >
        <CardContent
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            p: 3,
            minHeight: {xs: 'unset', lg: 520},
          }}
        >
          <AnimatedBackground primarycolor={primaryColor} />

          {/* Icône centrale avec animation */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
              mt: 2,
            }}
          >
            <Box
              sx={{
                'width': 100,
                'height': 100,
                'borderRadius': '50%',
                'bgcolor': alpha(primaryColor, 0.15),
                'display': 'flex',
                'alignItems': 'center',
                'justifyContent': 'center',
                'position': 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  width: '120%',
                  height: '120%',
                  borderRadius: '50%',
                  border: `2px dashed ${alpha(primaryColor, 0.3)}`,
                  animation: 'rotate 20s linear infinite',
                },
                '@keyframes rotate': {
                  from: {transform: 'rotate(0deg)'},
                  to: {transform: 'rotate(360deg)'},
                },
              }}
            >
              <Iconify
                icon="mdi:puzzle-outline"
                width={50}
                sx={{color: primaryColor}}
              />
            </Box>
          </Box>

          {/* Titre */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              textAlign: 'center',
              mb: 1.5,
              color: 'text.primary',
            }}
          >
            {t('common.giftVoucher.customVoucher.title')}
          </Typography>

          {/* Description */}
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            {t('common.giftVoucher.customVoucher.description')}
          </Typography>

          {/* Points clés */}
          <Stack spacing={1.5} sx={{mb: 3, flexGrow: 1}}>
            {[
              {
                icon: 'mdi:check-circle',
                text: t('common.giftVoucher.customVoucher.features.choice'),
              },
              {
                icon: 'mdi:infinity',
                text: t('common.giftVoucher.customVoucher.features.unlimited'),
              },
              {
                icon: 'mdi:gift',
                text: t('common.giftVoucher.customVoucher.features.single'),
              },
            ].map((feature, index) => (
              <Stack
                key={index}
                direction="row"
                spacing={1.5}
                alignItems="center"
              >
                <Iconify
                  icon={feature.icon}
                  width={20}
                  sx={{color: primaryColor, flexShrink: 0}}
                />
                <Typography variant="body2" sx={{fontSize: '0.9rem'}}>
                  {feature.text}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {/* Bouton CTA */}
          <Button
            fullWidth
            variant="contained"
            size="medium"
            tabIndex={-1}
            endIcon={<Iconify icon="eva:arrow-forward-fill" aria-hidden="true" />}
            sx={{
              'bgcolor': primaryColor,
              'color': getContrastTextColor(primaryColor),
              'py': 1,
              'textTransform': 'none',
              'borderRadius': 2,
              'fontWeight': 600,
              'fontSize': '0.9rem',
              'boxShadow': `0 4px 12px ${alpha(primaryColor, 0.2)}`,
              'transition': 'all 0.2s ease',
              '&:hover': {
                bgcolor: alpha(primaryColor, 0.9),
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 16px ${alpha(primaryColor, 0.3)}`,
              },
            }}
          >
            {t('common.giftVoucher.customVoucher.buttonText')}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}

CustomVoucherCard.propTypes = {
  companyDetails: PropTypes.object.isRequired,
  onNavigate: PropTypes.func,
};

export default CustomVoucherCard;
